import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { serverurl } from '../../environments/environment';
import { closingContentChanges, GenericStreamListForUserSelection, GenericStreamListForUserUse, GenericStreamTag, GenericUserStream, GenericUserStreamResult, GenericUserStreamWithProgress } from '../interfaces/responsedata.ts/generic-streams';
import { UserGateProgressElement } from '../interfaces/userdata';
import { SnackService } from './snack.service';

@Injectable({
    providedIn: 'root'
})
export class GenericStreamService {
    http = inject(HttpClient);
    snack = inject(SnackService);

    async updateUserGateProgress(streamId: string): Promise<false | UserGateProgressElement[]> {
        return await new Promise((resolve) => {
            this.http.put(`${serverurl}/streams/generic-stream/user-gate`, { streamId }).subscribe({
                next: (data: any) => {
                    resolve(data as UserGateProgressElement[]);
                },
                error: (err) => {
                    this.snack.error(err);
                    resolve(false);
                }
            });
        });
    }
    async getGenericStreamResults(): Promise<any> {
        return await new Promise((resolve) => {
            this.http.get(`${serverurl}/streams/generic-stream/data/stream-results`).subscribe({
                next: (data: any) => {

                    resolve(data);
                },
                error: (err) => {
                    this.snack.error(err);
                    resolve(false);
                }
            });
        });
    }
    async saveGenricResultContent(streamId: string, stepId: string, content: string, changes: closingContentChanges[]): Promise<boolean> {
        return await new Promise((resolve) => {
            this.http.post(`${serverurl}/streams/generic-stream/user/save-closing-content`, {
                streamId,
                stepId,
                content,
                changes
            }).subscribe({
                next: (data: any) => {
                    this.snack.success(data.msg);
                    resolve(true);
                },
                error: (err) => {
                    this.snack.error(err);
                    resolve(false);
                }
            });
        });
    }
    async getHtmlDataByUser(streamId: string, stepId: string, userId: string): Promise<any | false> {
        return await new Promise((resolve) => {
            this.http.get(`${serverurl}/knowledgehub/generic-closing-meta/changed/${streamId}/${stepId}/${userId}`).subscribe({
                next: async (data: any) => {
                    resolve(data.html as any);
                },
                error: (err) => {
                    this.snack.error(err);
                    resolve(false);
                }
            });
        });
    }
    async getHtmlDataByMetaData(streamId: string, stepId: string, userId: string): Promise<GenericUserStreamResult | false> {
        return await new Promise((resolve) => {
            this.http.get(`${serverurl}/knowledgehub/generic-closing-meta/${streamId}/${stepId}/${userId}`).subscribe({
                next: async (data: any) => {
                    resolve(data as any);
                },
                error: (err) => {
                    this.snack.error(err);
                    resolve(false);
                }
            });
        });
    }
    async getHtmlData(htmlAssetId: string): Promise<any | false> {
        return await new Promise((resolve) => {
            this.http.get(`${serverurl}/knowledgehub/generic-closing-index/${htmlAssetId}`).subscribe({
                next: async (data: any) => {
                    resolve(data as any);
                },
                error: (err) => {
                    this.snack.error(err);
                    resolve(false);
                }
            });
        });
    }
    async generateLpLink(streamId: string): Promise<string | false> {
        return await new Promise((resolve) => {
            this.http.post(`${serverurl}/streams/generatelplink`, { streamId }).subscribe({
                next: async (data: any) => {
                    this.snack.success(data.msg);
                    resolve(data.refId as string);
                },
                error: (err) => {
                    this.snack.error(err);
                    resolve(false);
                }
            });
        });
    }
    async getGenericStreamNames(): Promise<false | GenericStreamTag[]> {
        return await new Promise((resolve) => {
            this.http.get(`${serverurl}/streams/generic-stream/data/tags`).subscribe({
                next: (data: any) => {
                    resolve(data as GenericStreamTag[]);
                },
                error: (err) => {
                    this.snack.error(err);
                    resolve(false);
                }
            });
        });
    }
    async getGenericStreams(): Promise<false | GenericStreamListForUserSelection[]> {
        return await new Promise((resolve) => {
            this.http.get(`${serverurl}/streams/generic-stream`).subscribe({
                next: async (data: any) => {
                    resolve(data as GenericStreamListForUserSelection[]);
                },
                error: (err) => {
                    this.snack.error(err);
                    resolve(false);
                }
            });
        });
    }
    async getOneGenericStream(id: string): Promise<false | GenericStreamListForUserUse> {
        return await new Promise((resolve) => {
            this.http.get(`${serverurl}/streams/generic-stream/${id}`).subscribe({
                next: (data: any) => {
                    resolve(data as GenericStreamListForUserUse);
                },
                error: (err) => {
                    this.snack.error(err);
                    resolve(false);
                }
            });
        });
    }

    async getGenericUserStreamsByIds(
        userId: string,
        configIds: string[],
    ): Promise<boolean | GenericStreamListForUserUse[]> {
        const params = new HttpParams().set('configIds', configIds.join(','));

        if (!configIds.length) {
            return [];
        }

        return await new Promise((resolve) => {
            this.http.get<boolean | GenericStreamListForUserUse[]>(
                `${serverurl}/streams/generic-stream/user/${userId}/multiple`,
                { params },
            ).subscribe({
                next: (data) => {
                    resolve(data);
                },
                error: (err) => {
                    this.snack.error(err);
                    resolve(false);
                }
            });
        });
    }

    // GENERIC USER STREAM STUFF
    async createGenericUserStream(id: string, name: string): Promise<false | string> {
        return await new Promise((resolve) => {
            this.http.post(`${serverurl}/streams/generic-stream/user`,
                { streamConfigId: id, streamNameUser: name }).subscribe({
                    next: (data: any) => {
                        resolve(data.id as string);
                    },
                    error: (err) => {
                        this.snack.error(err);
                        resolve(false);
                    }
                });
        });
    }
    async getGenericUserStreams(): Promise<false | GenericUserStream[]> {
        return await new Promise((resolve) => {
            this.http.get(`${serverurl}/streams/generic-stream/user?t=${Date.now()}`).subscribe({
                next: (data: any) => {

                    data.forEach((stream: any) => {
                        stream.finished = stream.finished === 0 ? false : true;
                    });

                    resolve(data as GenericUserStream[]);
                },
                error: (err) => {
                    this.snack.error(err);
                    resolve(false);
                }
            });
        });
    }
    async getOneGenericUserStream(id: string): Promise<false | GenericUserStreamWithProgress> {
        return await new Promise((resolve) => {
            const date = new Date().getTime();
            this.http.get(`${serverurl}/streams/generic-stream/user/${id}?t=${date}`).subscribe({
                next: (data: any) => {
                    resolve(data as GenericUserStreamWithProgress);
                },
                error: (err) => {
                    this.snack.error(err);
                    resolve(false);
                }
            });
        });
    }
    async deleteGenericUserStream(id: string): Promise<boolean> {
        return await new Promise((resolve) => {
            this.http.delete(`${serverurl}/streams/generic-stream/user/${id}`).subscribe({
                next: () => {
                    resolve(true);
                },
                error: (err) => {
                    this.snack.error(err);
                    resolve(false);
                }
            });
        });
    }

    // GENERIC USER PROGRESS STUFF
    async createGenericUserStreamProgress(streamId: string, stepId: string, threadId: string): Promise<boolean> {
        return await new Promise((resolve) => {
            this.http.post(`${serverurl}/streams/generic-stream/user/progress`, { streamId, stepId, threadId }).subscribe({
                next: () => {
                    resolve(true);
                },
                error: (err) => {
                    this.snack.error(err);
                    resolve(false);
                }
            });
        });
    }
    async updateGenericUserStreamCurrentStep(streamId: string, stepId: string): Promise<boolean> {
        return await new Promise((resolve) => {
            this.http.put(`${serverurl}/streams/generic-stream/user/step`, { streamId, stepId }).subscribe({
                next: () => {
                    resolve(true);
                },
                error: (err) => {
                    this.snack.error(err);
                    resolve(false);
                }
            });
        });
    }
    async updateGenericUserStreamHistory(streamId: string, history: any, threadId: string): Promise<boolean> {
        return await new Promise((resolve) => {
            this.http.put(`${serverurl}/streams/generic-stream/user/history`, { streamId, history, threadId }).subscribe({
                next: () => {
                    resolve(true);
                },
                error: (err) => {
                    // this.snack.error(err);
                    resolve(false);
                }
            });
        });
    }
    async updateGenericUserStreamResults(streamId: string, results: any): Promise<boolean> {
        return await new Promise((resolve) => {
            this.http.put(`${serverurl}/streams/generic-stream/user/results`, { streamId, results }).subscribe({
                next: () => {
                    resolve(true);
                },
                error: (err) => {
                    this.snack.error(err);
                    resolve(false);
                }
            });
        });
    }
    async updateGenericUserStreamFinished(streamId: string): Promise<boolean> {
        return await new Promise((resolve) => {
            this.http.put(`${serverurl}/streams/generic-stream/user/finished`, { streamId }).subscribe({
                next: () => {
                    resolve(true);
                },
                error: (err) => {
                    this.snack.error(err);
                    resolve(false);
                }
            });
        });
    }

    async getFinishedUserStreams(founderId: string): Promise<GenericUserStreamWithProgress[] | boolean> {
        return await new Promise((resolve) => {
            this.http.get<GenericUserStreamWithProgress[]>(`${serverurl}/streams/generic-stream/${founderId}/finished`).subscribe({
                next: (result) => {
                    resolve(result);
                },
                error: (err) => {
                    this.snack.error(err);
                    console.log(err);
                    resolve(false);
                }
            });
        });
    }
    // =================================== FILE STUFF
    async createFileFromResultData(name: string, data: string, type: string, variant: string): Promise<any | boolean> {
        return new Promise(async (resolve) => {

            this.http.post(serverurl + '/knowledgehub/createpdf', { name, data, type, variant }).subscribe({
                next: async (responsedata: any) => {
                    resolve({ fileData: responsedata.filedata, buffer: responsedata.resultBuffer });
                },
                error: (err) => {
                    console.log(err);

                    this.snack.error(err);
                    resolve(false);
                }
            });
        });
    }
}
